<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-2xl p-6">
      <h3 class="text-lg font-semibold mb-4">{{ title }}</h3>

      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">{{ isSoftwareStep ? 'Выберите ПО' : 'Выберите продукт' }}</label>
        <!-- В шаге «Используемое ПО» список свой: только номенклатура групп ПО -->
        <v-select
          :options="isSoftwareStep ? softwareOptions : productOptions"
          label="label"
          :filter="(options) => options || []"
          :modelValue="selectedProduct"
          @update:modelValue="$emit('update:selectedProduct', $event)"
          @search="onSearch"
          placeholder="Начните вводить название или артикул..."
          :loading="isSoftwareStep ? softwareLoading : loading"
        >
          <template #no-options>
            <span v-if="isSoftwareStep ? softwareLoading : loading">Загружается...</span>
            <span v-else>Ничего не найдено</span>
          </template>
        </v-select>
        <p v-if="isSoftwareStep && softwareGroupMissing" class="text-sm text-orange-700 mt-1">{{ softwareGroupMissingText }}</p>
        <p v-if="isSoftwareStep && softwareError" class="text-sm text-red-500 mt-1">{{ softwareError }}</p>
      </div>

      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">Количество</label>
        <!-- В шаге «Используемое ПО» количество всегда 1: ПО не складской расходник -->
        <input
          v-if="isSoftwareStep"
          value="1"
          type="text"
          readonly
          class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
        />
        <input
          v-else
          :value="productCount"
          @input="$emit('update:productCount', $event.target.value)"
          type="number"
          min="0.001"
          class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Введите количество"
        />
        <p v-if="isSoftwareStep" class="text-sm text-gray-500 mt-1">В шаге «Используемое ПО» количество каждого ПО — 1 шт</p>
      </div>

      <div v-if="!isSoftwareStep" class="mb-4">
        <label class="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            :checked="isSingle"
            @change="$emit('update:isSingle', $event.target.checked)"
            class="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <span class="ml-2 text-sm font-medium text-gray-700">Серийная продукция</span>
        </label>
      </div>

      <p v-if="error" class="text-red-500 text-sm mt-1 mb-4">{{ error }}</p>

      <div class="flex justify-end space-x-2">
        <button
          type="button"
          class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          @click="onSave"
        >
          Сохранить
        </button>
        <button
          type="button"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
          @click="$emit('close')"
        >
          Отмена
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import vSelect from 'vue-select';

// Подсказка, когда в номенклатуре нет ни одной группы ПО
const SOFTWARE_GROUP_MISSING_TEXT = 'Нет группы «УСЛУГИ ОКАЗЫВАЕМЫЕ»';

export default {
  components: { vSelect },
  data() {
    return {
      softwareOptions: [], // Номенклатура групп ПО для шага «Используемое ПО»
      softwareLoading: false,
      softwareGroupMissing: false, // В номенклатуре нет ни одной группы ПО
      softwareError: '',
      softwareSearchTimer: null,
      softwareRequestId: 0, // Номер последнего запроса: ответы на устаревшие поиски не применяем
      softwareGroupMissingText: SOFTWARE_GROUP_MISSING_TEXT,
    };
  },
  mounted() {
    if (this.isSoftwareStep) {
      this.loadSoftware('');
    }
  },
  beforeUnmount() {
    clearTimeout(this.softwareSearchTimer);
  },
  props: {
    productOptions: { type: Array, default: () => [] },
    selectedProduct: { type: Object, default: null },
    productCount: { type: [Number, String], default: 1 },
    error: { type: String, default: '' },
    editingIndex: { type: Number, default: null },
    loading: { type: Boolean, default: false },
    isSingle: { type: Boolean, default: false },
    // Тип шага, в который добавляется продукт: от него зависят ограничения формы
    stepType: { type: String, default: null }
  },
  emits: [
    'update:selectedProduct',
    'update:productCount',
    'update:isSingle',
    'save',
    'close',
    'reload-products',
    'search'
  ],
  computed: {
    isSoftwareStep() {
      return this.stepType === 'software';
    },
    title() {
      const subject = this.isSoftwareStep ? 'ПО' : 'продукт';
      return this.editingIndex === null ? `Добавить ${subject}` : `Редактировать ${subject}`;
    }
  },
  methods: {
    onSearch(search) {
      if (!this.isSoftwareStep) {
        this.$emit('search', search);
        return;
      }

      clearTimeout(this.softwareSearchTimer);
      this.softwareSearchTimer = setTimeout(() => this.loadSoftware(search), 350);
    },

    // Номенклатура групп ПО: обычный список продукции сюда не подходит — в нём всё подряд
    async loadSoftware(search) {
      const requestId = ++this.softwareRequestId;
      this.softwareLoading = true;
      this.softwareError = '';
      try {
        const { data } = await axios.post('/api/product/getProducts', {
          page: 1,
          per_page: 50,
          search: search?.trim() || '',
          software_only: true,
        });
        // Пока ждали ответ, пользователь ввёл новый поиск — этот ответ устарел
        if (requestId !== this.softwareRequestId) return;
        this.softwareGroupMissing = Boolean(data?.software_group_missing);
        this.softwareOptions = (data?.data?.data || []).map((item) => ({
          id: item.id,
          name: item.name || '',
          sku: item.sku || '',
          unit_short_name: item.unit?.short_name || 'шт',
          accounting_type: item.accounting_type || null,
          label: item.sku ? `${item.sku} - ${item.name}` : (item.name || ''),
          value: item.id,
        }));
      } catch (error) {
        console.error('Ошибка загрузки списка ПО:', error);
        if (requestId !== this.softwareRequestId) return;
        this.softwareOptions = [];
        this.softwareError = 'Не удалось загрузить список ПО, попробуйте ещё раз';
      } finally {
        if (requestId === this.softwareRequestId) {
          this.softwareLoading = false;
        }
      }
    },

    onSave() {
      if (this.isSoftwareStep) {
        // Количество ПО фиксировано, серийного учёта у него нет
        this.$emit('update:productCount', 1);
        this.$emit('update:isSingle', false);
        this.$emit('save');
        return;
      }

      const rounded = Math.round(parseFloat(this.productCount) * 1000) / 1000;
      if (!isNaN(rounded)) {
        this.$emit('update:productCount', rounded);
      }
      this.$emit('save');
    }
  }
};
</script>
